import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ReceiptText,
  Server,
  RadioTower,
  Search,
  Scale,
  FolderOpen,
  Sparkles,
  ShieldCheck,
  LogOut,
  Menu,
  ChevronDown,
  CreditCard,
  Lock,
  Presentation,
  Gauge,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo, Footer } from "@/components/common/Brand";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fmtNum } from "@/lib/format";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
  { section: "Tools" },
  { to: "/tools/pajak", label: "Kalkulator Pajak", icon: ReceiptText, testid: "sidebar-nav-pajak", hint: "PPh 21 · 23 · 4(2)" },
  { to: "/tools/hosting", label: "COGS Hosting/VPS", icon: Server, testid: "sidebar-nav-hosting", hint: "Server & paket" },
  { to: "/tools/isp", label: "COGS Jaringan ISP", icon: RadioTower, testid: "sidebar-nav-isp", hint: "Per wilayah" },
  { to: "/tools/ekualizer", label: "Ekualizer Pajak", icon: Scale, testid: "sidebar-nav-ekualizer", hint: "Multi-PT (Admin)", admin: true, locked: true },
  { to: "/tools/kartu-kredit", label: "Tagihan Kartu Kredit", icon: CreditCard, testid: "sidebar-nav-kartu-kredit", hint: "Reimburse (Super admin)", admin: true, locked: true },
  { to: "/tools/meeting", label: "Meeting Eddy", icon: Presentation, testid: "sidebar-nav-meeting", hint: "Briefing & voting (Admin)", admin: true, locked: true },
  { to: "/tools/scraper", label: "Scraping Kontak", icon: Search, testid: "sidebar-nav-scraper", hint: "LinkedIn (Admin)", admin: true },
  { to: "/tools/monitor-ai", label: "Monitor Token AI", icon: Gauge, testid: "sidebar-nav-monitor-ai", hint: "Saldo & biaya (Admin)", admin: true, locked: true },
  { section: "Data" },
  { to: "/saved", label: "Perhitungan Tersimpan", icon: FolderOpen, testid: "sidebar-nav-saved" },
  { to: "/usage", label: "Pemakaian Token AI", icon: Sparkles, testid: "sidebar-nav-usage" },
  { to: "/admin", label: "Pengaturan Admin", icon: ShieldCheck, testid: "sidebar-nav-admin", admin: true },
];

function NavList({ onNavigate }) {
  const { isAdmin } = useAuth();
  return (
    <nav className="flex flex-col gap-1">
      {NAV.filter((i) => !i.admin || isAdmin).map((item, idx) =>
        item.section ? (
          <div key={`s-${idx}`} className="eyebrow px-3 pt-5 pb-1.5">
            {item.section}
          </div>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.testid}
            className={({ isActive }) =>
              `nav-link group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm border ${
                isActive
                  ? "bg-[hsl(43_40%_14%)] border-[color:var(--hairline)] text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-[hsl(220_10%_11%)]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"}`} />
                <span className="flex-1">
                  <span className="block leading-tight">{item.label}</span>
                  {item.hint && <span className="block text-[11px] text-muted-foreground/80">{item.hint}</span>}
                </span>
                {item.locked && <Lock className="h-3 w-3 text-[hsl(43_50%_58%)]" aria-label="Admin only" />}
                {isActive && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
              </>
            )}
          </NavLink>
        )
      )}
    </nav>
  );
}

function QuotaMini() {
  const { quota } = useAuth();
  if (!quota) return null;
  const pct = quota.limit ? Math.min(100, (quota.used / quota.limit) * 100) : 0;
  return (
    <NavLink to="/usage" className="block rounded-xl border border-border bg-[hsl(220_14%_6%)] p-3 card-hover" data-testid="sidebar-token-quota">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Token AI bulan ini</span>
        <Sparkles className="h-3.5 w-3.5 text-primary" />
      </div>
      <div className="num text-sm mt-1.5">
        {fmtNum(quota.used)} <span className="text-muted-foreground">/ {fmtNum(quota.limit)}</span>
      </div>
      <Progress value={pct} className="h-1.5 mt-2 bg-[hsl(220_10%_14%)]" />
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const doLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="relative z-[1] min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[264px] shrink-0 flex-col border-r border-border bg-[hsl(220_14%_6%)] fixed inset-y-0 left-0 z-30">
        <div className="h-16 px-5 flex items-center border-b border-border">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-4 scroll-gold">
          <NavList />
        </div>
        <div className="p-3 border-t border-border">
          <QuotaMini />
        </div>
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[280px] bg-[hsl(220_14%_6%)] border-border p-0" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigasi</SheetTitle>
          <div className="h-16 px-5 flex items-center border-b border-border">
            <Logo />
          </div>
          <div className="px-3 pb-4 overflow-y-auto">
            <NavList onNavigate={() => setOpen(false)} />
          </div>
          <div className="p-3 border-t border-border">
            <QuotaMini />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col lg:pl-[264px]">
        <header className="h-16 sticky top-0 z-20 border-b border-border bg-[hsl(220_14%_5%)] flex items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="topbar-mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="lg:hidden">
            <Logo compact />
          </div>
          <div className="hidden md:block text-xs text-muted-foreground tracking-wide" data-testid="topbar-breadcrumb">
            {loc.pathname.startsWith("/tools") ? "Tools" : "Workspace"} <span className="text-[hsl(43_45%_55%)] mx-1.5">/</span>
            <span className="text-foreground">
              {NAV.find((i) => i.to && loc.pathname.startsWith(i.to))?.label || "Dashboard"}
            </span>
          </div>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="press flex items-center gap-2.5 rounded-lg border border-border bg-[hsl(220_12%_8%)] pl-1.5 pr-3 py-1.5 hover:border-[color:var(--hairline)]"
                data-testid="topbar-user-menu-button"
              >
                <span className="h-7 w-7 rounded-md bg-[hsl(43_40%_16%)] border border-[color:var(--hairline)] flex items-center justify-center text-xs font-semibold text-primary uppercase">
                  {(user?.display_name || user?.email || "?").slice(0, 2)}
                </span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-xs text-foreground max-w-[200px] truncate">{user?.email}</span>
                  <span className="block text-[10px] uppercase tracking-widest text-[hsl(43_50%_58%)]">
                    {isAdmin ? "Administrator" : "Member"}
                  </span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/saved")} data-testid="user-menu-saved">
                <FolderOpen className="h-4 w-4 mr-2" /> Perhitungan Tersimpan
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/usage")} data-testid="user-menu-usage">
                <Sparkles className="h-4 w-4 mr-2" /> Pemakaian Token AI
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => nav("/admin")} data-testid="user-menu-admin">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Pengaturan Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="text-[hsl(0_72%_62%)]" data-testid="user-menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6">{children}</main>
        <Footer />
      </div>
    </div>
  );
}
